/**
 * Date utility functions for consistent date formatting across the application
 */

/**
 * Formats a date for storage in YYYY-MM-DD hh:mm:ss format using Europe/Stockholm timezone
 * This is the standard format used throughout the application for database storage
 * 
 * @param date - The date to format
 * @returns Formatted date string in YYYY-MM-DD hh:mm:ss format in Stockholm timezone
 * 
 * @example
 * formatDateForStorage(new Date()) // "2024-01-15 14:30:25"
 */
export const formatDateForStorage = (date: Date): string => {
    // Convert to Stockholm timezone
    const stockholmDate = new Date(date.toLocaleString("en-US", {timeZone: "Europe/Stockholm"}));
    const year = stockholmDate.getFullYear();
    const month = String(stockholmDate.getMonth() + 1).padStart(2, '0');
    const day = String(stockholmDate.getDate()).padStart(2, '0');
    const hours = String(stockholmDate.getHours()).padStart(2, '0');
    const minutes = String(stockholmDate.getMinutes()).padStart(2, '0');
    const seconds = String(stockholmDate.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Gets the current date formatted for storage using Stockholm timezone
 * 
 * @returns Current date in YYYY-MM-DD hh:mm:ss format
 */
export const getCurrentDateForStorage = (): string => {
    return formatDateForStorage(new Date());
};

/**
 * Converts a date string in YYYY-MM-DD hh:mm:ss format back to a Date object
 * Assumes the input is in Stockholm timezone
 * 
 * @param dateString - Date string in YYYY-MM-DD hh:mm:ss format
 * @returns Date object
 */
export const parseStorageDate = (dateString: string): Date => {
    // Parse the date string and create a Date object
    // The date string is assumed to be in Stockholm timezone
    const [datePart, timePart] = dateString.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes, seconds] = timePart.split(':').map(Number);
    
    // Create date in Stockholm timezone
    return new Date(year, month - 1, day, hours, minutes, seconds);
};
